import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface InfiniteScrollPage<T> {
  items: T[];
  total?: number;
  hasMore?: boolean;
}

const LOAD_MORE_THRESHOLD_PX = 120;

const findScrollableParent = (element: HTMLElement): HTMLElement | null => {
  let current = element.parentElement;
  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

const isNearBottom = (element: HTMLElement): boolean => {
  const scroller = findScrollableParent(element);
  if (!scroller) {
    return true;
  }
  return scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= LOAD_MORE_THRESHOLD_PX;
};

export interface InfiniteScrollProps<T> {
  fetchPage: (limit: number, offset: number) => Promise<InfiniteScrollPage<T>>;
  limit: number;
  renderItem: (item: T, index: number) => React.ReactNode;
  initialItems?: T[];
  initialOffset?: number;
  finishedState?: React.ReactNode;
  emptyState?: React.ReactNode;
  loadingState?: React.ReactNode;
  errorState?: React.ReactNode;
}

const InfiniteScroll = <T,>(props: InfiniteScrollProps<T>) => {
  const {
    fetchPage,
    limit,
    renderItem,
    initialOffset = 0,
    initialItems = [],
    finishedState = <></>,
    emptyState = <></>,
    loadingState = <></>,
    errorState = <></>
  } = props;

  const [items, setItems] = useState<T[]>(initialItems);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(initialOffset);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(false);

    const currentOffset = offsetRef.current;
    try {
      const response = await fetchPage(limit, currentOffset);
      const newItems = response.items ?? [];
      offsetRef.current = currentOffset + newItems.length;
      setItems((prev) => [...prev, ...newItems]);

      let nextHasMore: boolean;
      if (typeof response.hasMore === 'boolean') {
        nextHasMore = response.hasMore && newItems.length !== 0;
      } else if (response.total || response.total === 0) {
        nextHasMore = currentOffset + newItems.length < response.total && newItems.length !== 0;
      } else {
        nextHasMore = newItems.length === limit;
      }
      hasMoreRef.current = nextHasMore;
      setHasMore(nextHasMore);
    } catch {
      setError(true);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [fetchPage, limit]);

  useEffect(() => {
    setItems(initialItems);
    offsetRef.current = initialOffset;
    hasMoreRef.current = true;
    setHasMore(true);
    setError(false);
    void loadMore();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const scroller = findScrollableParent(el);

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first?.isIntersecting && isNearBottom(el)) void loadMore();
      },
      { root: scroller, threshold: 0 }
    );
    observer.observe(el);

    const scrollTarget: HTMLElement | Window = scroller ?? window;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = findScrollableParent(el) ?? document.documentElement;
      if (scrollHeight - scrollTop - clientHeight <= LOAD_MORE_THRESHOLD_PX) loadMore();
    };
    scrollTarget.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      scrollTarget.removeEventListener('scroll', handleScroll);
    };
  }, [loadMore, loading]);

  return (
    <>
      {items.map((item: T, index: number) => renderItem(item, index))}
      {error && errorState}
      {!error && loading && loadingState}
      {!error && !loading && items.length === 0 && !hasMore && emptyState}
      <div ref={sentinelRef} aria-hidden className="h-1" />
      {!error && !loading && !hasMore && items.length > 0 && finishedState}
    </>
  );
};

export default InfiniteScroll;
